import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Package, Tag, Star, Shield } from 'lucide-react';

interface StockLotCardProps {
  stockLot: {
    id: string;
    title: string;
    description: string;
    quantity: number;
    original_price: number;
    discounted_price: number;
    discount_percentage: number;
    images: string[];
    category: string;
    condition: string;
    expiry_date: string | null;
    location: string;
    supplier: {
      organization: string;
      location: string;
      verified: boolean;
    };
    created_at: string;
  };
}

const StockLotCard: React.FC<StockLotCardProps> = ({ stockLot }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysLeft = (expiryDate: string) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysLeft = stockLot.expiry_date ? getDaysLeft(stockLot.expiry_date) : null;

  return (
    <div className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group">
      <div className="relative">
        <img 
          src={stockLot.images[0] || 'https://images.pexels.com/photos/996329/pexels-photo-996329.jpeg'} 
          alt={stockLot.title}
          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
        />
        
        {/* Discount Badge */}
        <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
          -{stockLot.discount_percentage}% OFF
        </div>
        
        {/* Stock Lot Badge */}
        <div className="absolute top-2 right-2 bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
          STOCK LOT
        </div>
        
        {/* Urgency Badge */}
        {daysLeft && daysLeft <= 7 && (
          <div className="absolute bottom-2 left-2 bg-yellow-500 text-white px-2 py-1 rounded text-xs font-medium flex items-center">
            <Clock className="h-3 w-3 mr-1" />
            {daysLeft} days left
          </div>
        )}
      </div>
      
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-gray-900 text-lg line-clamp-2 flex-1">
            {stockLot.title}
          </h3>
          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs ml-2">
            {stockLot.condition}
          </span>
        </div>
        
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{stockLot.description}</p>
        
        {/* Quantity */}
        <div className="flex items-center mb-3">
          <Package className="h-4 w-4 text-gray-500 mr-1" />
          <span className="text-sm text-gray-600">{stockLot.quantity.toLocaleString()} units available</span>
        </div>
        
        {/* Pricing */}
        <div className="flex items-center space-x-2 mb-3">
          <span className="text-2xl font-bold text-green-600">
            ${stockLot.discounted_price.toFixed(2)}
          </span>
          <span className="text-lg text-gray-500 line-through">
            ${stockLot.original_price.toFixed(2)}
          </span>
          <span className="text-sm text-gray-600">per unit</span>
        </div>
        
        {/* Category */}
        <div className="flex items-center mb-3">
          <Tag className="h-4 w-4 text-gray-500 mr-1" />
          <span className="text-sm text-gray-600 capitalize">{stockLot.category}</span>
        </div>
        
        {/* Supplier Info */}
        <div className="border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center">
                <span className="font-medium text-gray-900">{stockLot.supplier.organization}</span>
                {stockLot.supplier.verified && (
                  <Shield className="h-4 w-4 text-green-500 ml-1" />
                )}
              </div>
              <div className="flex items-center text-xs text-gray-500">
                <MapPin className="h-3 w-3 mr-1" />
                {stockLot.supplier.location}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">Listed</div>
              <div className="text-xs text-gray-600">{formatDate(stockLot.created_at)}</div>
            </div>
          </div>
        </div>
        
        {/* Action Button */}
        <Link
          to={`/stock-lot/${stockLot.id}`}
          className="mt-4 w-full bg-orange-600 text-white py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors flex items-center justify-center"
        >
          View Details
        </Link>
      </div>
    </div>
  );
};

export default StockLotCard;